import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import DoodleJumpGame from './DoodleJumpGame';
import Terraria2Game from './Terraria2Game';
import './GameContainer.css';

const GameContainer = ({ isActive, onClose, accentColor = 'purple', defaultGame = 'terraria', className = '' }) => {
    const { language } = useLanguage();
    const isEn = language === 'en';

    const [selectedGame, setSelectedGame] = useState(defaultGame);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // Detect touch/mobile device once — used for height differences
    const [isTouchDevice] = useState(() => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (typeof window !== 'undefined' && window.innerWidth <= 768));
    const terrariaHeight = isTouchDevice ? '380px' : '560px';
    const doodleHeight = isTouchDevice ? '360px' : '420px';
    // Track which games have been initialized — once true, stays true forever
    const [initDoodle, setInitDoodle] = useState(false);
    const [initTerraria, setInitTerraria] = useState(false);
    const resizeTimerRef = useRef(null);
    const containerRef = useRef(null);

    // When expanding or switching, mark the currently selected game for initialization
    useEffect(() => {
        if (isExpanded || isFullscreen) {
            if (selectedGame === 'doodle' && !initDoodle) setInitDoodle(true);
            if (selectedGame === 'terraria' && !initTerraria) setInitTerraria(true);
        }
    }, [isExpanded, isFullscreen, selectedGame, initDoodle, initTerraria]);

    const handleExpand = useCallback(() => {
        const next = !isExpanded;
        setIsExpanded(next);
        if (next && !hasBeenOpened) setHasBeenOpened(true);
    }, [isExpanded, hasBeenOpened]);

    const handleSwitch = useCallback((e, game) => {
        e.stopPropagation();
        setSelectedGame(game);
        if (!isExpanded) {
            setIsExpanded(true);
            if (!hasBeenOpened) setHasBeenOpened(true);
        }
    }, [isExpanded, hasBeenOpened]);

    const handleFullscreen = useCallback((e) => {
        e.stopPropagation();
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            if (!isExpanded) {
                setIsExpanded(true);
                if (!hasBeenOpened) setHasBeenOpened(true);
            }
            containerRef.current.requestFullscreen().catch(err => {
                console.warn('Fullscreen request failed:', err);
            });
        } else {
            document.exitFullscreen();
        }
    }, [isExpanded, hasBeenOpened]);

    // Listen for fullscreen change events (including Escape key exit)
    useEffect(() => {
        const onFsChange = () => {
            const isFull = !!document.fullscreenElement;
            setIsFullscreen(isFull);
            setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
        };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    // After expanding or switching games, dispatch resize so canvases recalculate dimensions
    useEffect(() => {
        if (!hasBeenOpened) return;
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);

        if (isExpanded) {
            resizeTimerRef.current = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 400);
        }

        return () => {
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        };
    }, [isExpanded, hasBeenOpened, selectedGame]);

    const accentPalette = {
        purple: { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.35)' },
        cyan:   { color: '#06b6d4', glow: 'rgba(6, 182, 212, 0.35)' },
        blue:   { color: '#3b82f6', glow: 'rgba(59, 130, 246, 0.35)' },
        red:    { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.35)' },
        green:  { color: '#1bd96a', glow: 'rgba(27, 217, 106, 0.35)' },
        white:  { color: '#ffffff', glow: 'rgba(255, 255, 255, 0.3)' }
    };
    const accent = accentPalette[accentColor] || accentPalette.purple;

    // Appear only when active (after module starts, like music)
    if (!isActive) return null;

    return (
        <div
            ref={containerRef}
            className={`game-panel-root ${isExpanded ? 'expanded' : ''} ${isFullscreen ? 'is-fullscreen' : ''} ${className}`}
        >
            {/* Header bar: tabs on the left, controls on the right */}
            <div className="game-panel-header" onClick={handleExpand}>
                {/* Left: Segmented Capsule Switcher */}
                <div className="game-panel-left">
                    <div className="game-tabs-capsule" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className={`game-tab-btn ${selectedGame === 'doodle' ? 'active' : ''}`}
                            onClick={(e) => handleSwitch(e, 'doodle')}
                            style={selectedGame === 'doodle' ? { background: accent.color, color: '#000000', boxShadow: `0 2px 10px ${accent.glow}` } : {}}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            <span>DOODLE</span>
                        </button>
                        <button
                            type="button"
                            className={`game-tab-btn ${selectedGame === 'terraria' ? 'active' : ''}`}
                            onClick={(e) => handleSwitch(e, 'terraria')}
                            style={selectedGame === 'terraria' ? { background: accent.color, color: '#000000', boxShadow: `0 2px 10px ${accent.glow}` } : {}}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M3 9h18M9 21V9" />
                            </svg>
                            <span>TERRARIUM</span>
                        </button>
                    </div>
                </div>

                {/* Right: Controls & toggle button */}
                <div className="game-panel-right">
                    {/* Fullscreen toggle button */}
                    {(isExpanded || isFullscreen) && (
                        <button
                            type="button"
                            className="game-action-btn"
                            onClick={handleFullscreen}
                            title={isFullscreen ? (isEn ? 'Exit fullscreen' : 'Выйти из полноэкранного') : (isEn ? 'Fullscreen' : 'Во весь экран')}
                        >
                            {isFullscreen ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                </svg>
                            ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                </svg>
                            )}
                        </button>
                    )}

                    {/* Toggle Expand/Collapse button matching .pov-toggle-btn */}
                    <button
                        type="button"
                        className="game-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExpand();
                        }}
                    >
                        <span>{isExpanded ? (isEn ? 'Collapse' : 'Свернуть') : (isEn ? 'Expand' : 'Развернуть')}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Subtle top neon border accent when open */}
            {(isExpanded || isFullscreen) && (
                <div
                    style={{
                        height: '2px',
                        background: `linear-gradient(90deg, transparent 0%, ${accent.color} 50%, transparent 100%)`,
                        boxShadow: `0 0 10px ${accent.glow}`,
                        flexShrink: 0
                    }}
                />
            )}

            {/* Body container matching .bot-pov-body & .pov-viewport-container */}
            {hasBeenOpened && (
                <div className="game-panel-body">
                    <div
                        className="game-viewport-frame"
                        style={{
                            height: isFullscreen ? '100%' : isExpanded ? (selectedGame === 'terraria' ? terrariaHeight : doodleHeight) : '0px',
                            opacity: isExpanded ? 1 : 0
                        }}
                    >
                        {/* Doodle Jump — once initialized, stays alive forever */}
                        <div style={{
                            display: selectedGame === 'doodle' ? 'block' : 'none',
                            width: '100%',
                            height: isFullscreen ? '100%' : doodleHeight
                        }}>
                            <DoodleJumpGame
                                isActive={initDoodle}
                                accentColor={accentColor}
                                onClose={onClose}
                            />
                        </div>

                        {/* Terrarium — once initialized, stays alive forever */}
                        <div style={{
                            display: selectedGame === 'terraria' ? 'block' : 'none',
                            width: '100%',
                            height: isFullscreen ? '100%' : terrariaHeight
                        }}>
                            <Terraria2Game
                                isActive={initTerraria}
                                isFullscreen={isFullscreen}
                                onClose={onClose}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GameContainer;
